import React, { useState, useEffect } from "react";
import { Constants } from "@eagerdog/constants";
import { IDog, IDogRun } from "@eagerdog/interfaces";

import Button from "../../../components/Button/Button";
import DatePick from "../../../components/DatePick/DatePick";
import Dropdown from "../../../components/Dropdown/Dropdown";
import Input from "../../../components/Input/Input";
import TextArea from "../../../components/TextArea/TextArea";

import { formatRunDate, parseRunDate, runOption, runOptions } from "./runFormFields";

import "./RallyRunForm.scss";

interface IProps {
  dog: IDog;
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  editRun?: IDogRun | null;
}

const RALLY_CLASSES = [
  { value: "Novice A", label: "Novice A" },
  { value: "Novice B", label: "Novice B" },
  { value: "Intermediate", label: "Intermediate" },
  { value: "Advanced A", label: "Advanced A" },
  { value: "Advanced B", label: "Advanced B" },
  { value: "Excellent A", label: "Excellent A" },
  { value: "Excellent B", label: "Excellent B" },
  { value: "Master", label: "Master" }
];

const RallyRunForm: React.FC<IProps> = ({
  dog,
  onSubmit,
  onCancel,
  isSubmitting,
  editRun
}) => {
  const [rallyClass, setRallyClass] = useState("");
  const [score, setScore] = useState("");
  const [resultStatus, setResultStatus] = useState("");
  const [trialDate, setTrialDate] = useState("");
  const [judgeNumber, setJudgeNumber] = useState("");
  const [judgeName, setJudgeName] = useState("");
  const [eventName, setEventName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (editRun) {
      setRallyClass(editRun.class || "");
      setScore(editRun.score !== undefined ? String(editRun.score) : "");
      setResultStatus(editRun.result_status || "");
      setTrialDate(editRun.trial_date ? new Date(editRun.trial_date).toISOString().split('T')[0] : "");
      setJudgeNumber(editRun.judge_number || "");
      setJudgeName(editRun.judge_name || "");
      setEventName(editRun.event_name || "");
      setCity(editRun.location?.city || "");
      setState(editRun.location?.state || "");
      setNotes(editRun.notes || "");
    }
  }, [editRun]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Parse the date and set it to noon local time to avoid timezone issues
    const [year, month, day] = trialDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day, 12, 0, 0);

    const formData: any = {
      dogId: dog._id,
      rallyClass,
      score: parseFloat(score),
      resultStatus,
      trialDate: localDate,
      judgeNumber: judgeNumber || undefined,
      judgeName: judgeName || undefined,
      eventName: eventName || undefined,
      location: city || state ? { city, state } : undefined,
      notes: notes || undefined
    };

    if (editRun) {
      formData.run_id = editRun._id;
    }

    await onSubmit(formData);
  };

  return (
    <form className="rallyRunForm" onSubmit={handleSubmit}>
      <h2>{editRun ? 'Edit' : 'Add'} AKC Rally Run</h2>
      <p className="dogName">For: {dog.call_name}</p>

      <div className="formGrid">
        <div className="formGroup">
          <Dropdown
            label="Class *"
            placeholder="Select class..."
            options={runOptions(RALLY_CLASSES, "Select class...")}
            value={runOption(runOptions(RALLY_CLASSES, "Select class..."), rallyClass)}
            onChange={(_e, option) => setRallyClass(option.id)}
          />
        </div>

        <div className="formGroup">
          <Input
            label="Score * (0-100)"
            id="score"
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={score}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setScore(e.target.value)}
            placeholder="e.g., 85.5"
            required
          />
          <span className="fieldHelp">Half points allowed (e.g., 85.5)</span>
        </div>

        <div className="formGroup">
          <Dropdown
            label="Result *"
            placeholder="Select result..."
            options={runOptions([
              { value: Constants.run_result_status.qualified, label: "Qualified" },
              { value: Constants.run_result_status.not_qualified, label: "Not Qualified" },
            ], "Select result...")}
            value={runOption(runOptions([
              { value: Constants.run_result_status.qualified, label: "Qualified" },
              { value: Constants.run_result_status.not_qualified, label: "Not Qualified" },
            ], "Select result..."), resultStatus)}
            onChange={(_e, option) => setResultStatus(option.id)}
          />
        </div>

        <div className="formGroup">
          <DatePick
            label="Trial Date *"
            placeholder="Select date"
            value={parseRunDate(trialDate)}
            onChange={(date: Date | null) => setTrialDate(formatRunDate(date))}
          />
        </div>

        <div className="formGroup">
          <Input
            label="Judge Number"
            id="judgeNumber"
            value={judgeNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJudgeNumber(e.target.value)}
            placeholder="e.g., 12345"
          />
        </div>

        <div className="formGroup">
          <Input
            label="Judge Name"
            id="judgeName"
            value={judgeName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJudgeName(e.target.value)}
            placeholder="e.g., Jane Smith"
          />
        </div>

        <div className="formGroup fullWidth">
          <Input
            label="Event Name"
            id="eventName"
            value={eventName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventName(e.target.value)}
            placeholder="e.g., Spring Rally Trial"
          />
        </div>

        <div className="formGroup">
          <Input
            label="City"
            id="city"
            value={city}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
            placeholder="e.g., Portland"
          />
        </div>

        <div className="formGroup">
          <Input
            label="State"
            id="state"
            value={state}
            maxLength={2}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setState(e.target.value.toUpperCase())}
            placeholder="CA"
          />
        </div>

        <div className="formGroup fullWidth">
          <TextArea
            className="runNotes"
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes..."
          />
        </div>
      </div>

      <div className="formActions">
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : (editRun ? "Update Run" : "Add Run")}
        </Button>
      </div>
    </form>
  );
};

export default RallyRunForm;

